import { createContext, useState, ReactNode, useContext, useRef, useEffect } from 'react';
import { AuthBottomSheet } from "@/components/auth/AuthBottomSheet";
import { useRouterState } from '@tanstack/react-router';

interface AuthModalContextType {
    isAuthModalOpen: boolean;
    openAuthModal: (returnPath?: string) => void;
    closeAuthModal: () => void;
    returnToPath: string;
}

const AuthModalContext = createContext<AuthModalContextType | undefined>(undefined);

export function AuthModalProvider({ children }: { children: ReactNode }) {
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [returnToPath, setReturnToPath] = useState('');

    // Get current location from TanStack Router (pathname is WITHOUT basepath)
    const location = useRouterState({ select: (s) => s.location });
    const locationRef = useRef(location);

    // Keep ref updated with latest location
    useEffect(() => {
        locationRef.current = location;
    }, [location]);

    const openAuthModal = (returnPath?: string) => {
        setIsAuthModalOpen(true);
        if (returnPath) {
            setReturnToPath(returnPath);
        } else {
            // pathname from TanStack Router doesn't include basepath
            const current = locationRef.current;
            setReturnToPath(current.pathname + current.searchStr);
        }
    };

    const closeAuthModal = () => {
        setIsAuthModalOpen(false);
    };

    return (
        <AuthModalContext.Provider
            value={{
                isAuthModalOpen,
                openAuthModal,
                closeAuthModal,
                returnToPath
            }}
        >
            {children}
            <AuthBottomSheet
                isOpen={isAuthModalOpen}
                onClose={closeAuthModal}
                returnToPath={returnToPath}
            />
        </AuthModalContext.Provider>
    );
}

export const useAuthModal = (): AuthModalContextType => {
    const context = useContext(AuthModalContext);
    if (context === undefined) {
        throw new Error('useAuthModal must be used within an AuthModalProvider');
    }
    return context;
}; 